<script setup lang="ts">
import { ElCheckbox, ElImageViewer } from 'element-plus'
import { computed, ref } from 'vue'

interface ProductItem {
  nmID: number
  vendor_code: string
  photo: string
}

type Product = string | ProductItem

interface Props {
  products: Product[]
  selectedProducts: string[]
  searchQuery?: string
  existingIds?: string[]
}

interface Emits {
  (e: 'update:selectedProducts', value: string[]): void
}

const props = withDefaults(defineProps<Props>(), {
  searchQuery: '',
  existingIds: () => [],
})

const emit = defineEmits<Emits>()

function isProductObject(product: Product): product is ProductItem {
  return typeof product === 'object' && 'nmID' in product
}

function getProductId(product: Product): string {
  if (isProductObject(product)) {
    return String(product.nmID)
  }
  return product
}

const filteredProducts = computed(() => {
  if (!props.searchQuery) {
    return props.products
  }

  const query = props.searchQuery.toLowerCase()
  return props.products.filter((product) => {
    if (isProductObject(product)) {
      return (
        String(product.nmID).toLowerCase().includes(query)
        || product.vendor_code.toLowerCase().includes(query)
      )
    }
    return product.toLowerCase().includes(query)
  })
})

function isExistingProduct(productId: string): boolean {
  return props.existingIds.includes(productId)
}

function toggleSelection(productId: string) {
  const newSelection = [...props.selectedProducts]
  const index = newSelection.indexOf(productId)

  if (index === -1) {
    newSelection.push(productId)
  }
  else {
    newSelection.splice(index, 1)
  }

  emit('update:selectedProducts', newSelection)
}

// Lightbox state
const lightboxVisible = ref(false)
const lightboxImageUrl = ref('')

function openLightbox(imageUrl: string) {
  lightboxImageUrl.value = imageUrl
  lightboxVisible.value = true
}

function closeLightbox() {
  lightboxVisible.value = false
  lightboxImageUrl.value = ''
}
</script>

<template>
  <div class="product-selector">
    <div class="product-list-container">
      <div class="product-list">
        <div
          v-for="product in filteredProducts"
          :key="getProductId(product)"
          class="product-item"
          :class="{
            'existing-product': isExistingProduct(getProductId(product)),
          }"
          @click="
            !isExistingProduct(getProductId(product))
              && toggleSelection(getProductId(product))
          "
        >
          <div
            class="checkbox-container"
            @click.stop="toggleSelection(getProductId(product))"
          >
            <ElCheckbox
              :model-value="selectedProducts.includes(getProductId(product))"
              class="product-checkbox"
              @click.stop
              @change="toggleSelection(getProductId(product))"
            />
          </div>

          <!-- Product Image -->
          <div
            v-if="isProductObject(product) && product.photo"
            class="product-image"
            @click.stop="openLightbox(product.photo)"
          >
            <img
              :src="product.photo"
              :alt="product.vendor_code || String(product.nmID)"
              class="product-img"
            >
            <div class="product-image-overlay">
              <i class="icon-zoom-in text-white text-lg" />
            </div>
          </div>
          <div v-else class="product-image-placeholder">
            <i class="icon-image text-xl text-text-secondary" />
          </div>

          <!-- Product Info -->
          <div class="product-info">
            <div
              class="product-label"
              :class="{
                'selected': selectedProducts.includes(getProductId(product)),
                'existing-label': isExistingProduct(getProductId(product)),
              }"
            >
              <div class="product-row">
                <span class="product-field-label">{{ $t("wb_article") }}:</span>
                <span class="product-id">{{ getProductId(product) }}</span>
              </div>
              <div
                v-if="isProductObject(product) && product.vendor_code"
                class="product-row"
              >
                <span class="product-field-label">{{ $t("vendor_code") }}:</span>
                <span class="product-vendor">{{ product.vendor_code }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Lightbox -->
    <ElImageViewer
      v-if="lightboxVisible"
      :url-list="[lightboxImageUrl]"
      :initial-index="0"
      :hide-on-click-modal="true"
      @close="closeLightbox"
    />
  </div>
</template>

<style scoped>
.product-selector {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.product-list-container {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: white;
}

.product-list {
  padding: 0;
}

.product-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  gap: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.product-item:hover {
  background-color: #f9fafb;
}

.product-item:last-child {
  border-bottom: none;
}

.product-item.existing-product {
  opacity: 0.6;
  cursor: not-allowed;
}

.checkbox-container {
  flex-shrink: 0;
}

.product-checkbox {
  margin: 0;
}

.product-image {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  position: relative;
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}

.product-image:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.product-image:hover .product-image-overlay {
  opacity: 1;
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 8px;
}

.product-image-placeholder {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  color: #4b5563;
  transition: color 0.2s;
}

.product-label.selected {
  color: #1f2937;
}

.product-label.existing-label {
  color: #9ca3af;
}

.product-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.product-field-label {
  font-size: 0.75rem;
  color: #9ca3af;
  min-width: 115px;
  flex-shrink: 0;
}

.product-id {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1f2937;
}

.product-vendor {
  font-size: 0.8125rem;
  color: #4b5563;
  font-weight: 500;
}

.product-label.selected .product-id {
  color: var(--el-color-primary, #9117ef);
}

.product-label.selected .product-vendor {
  color: #1f2937;
}

.product-label.existing-label .product-id,
.product-label.existing-label .product-vendor,
.product-label.existing-label .product-field-label {
  color: #9ca3af;
}

/* Custom scrollbar */
.product-list-container::-webkit-scrollbar {
  width: 6px;
}

.product-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.product-list-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.product-list-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
